import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { endpoints } from "../apiConfig";
import { colors, fonts, space, radius, buttonPrimary, buttonSecondary, inputBase, pageHeading } from "../theme";
import PaymentModal, { PaymentNote, paymentNote, fmtDate } from "../components/PaymentModal";
import useIsMobile from "../useIsMobile";

export default function AdminPanel() {
  const [tenants, setTenants] = useState([]);
  const [selected, setSelected] = useState([]);
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [editingSubscriptionFor, setEditingSubscriptionFor] = useState(null);
  const [subscriptionForm, setSubscriptionForm] = useState({});
  const [version, setVersion] = useState("");
  const [editingCapsFor, setEditingCapsFor] = useState(null);
  const [capsForm, setCapsForm] = useState({});
  const [editingProfileFor, setEditingProfileFor] = useState(null);
  const [profileForm, setProfileForm] = useState({});
  const [showNotify, setShowNotify] = useState(false);
  const [defaultCaps, setDefaultCaps] = useState(null);
  const [defaultCapsForm, setDefaultCapsForm] = useState(null);
  const [defaultCapsSaved, setDefaultCapsSaved] = useState(false);
  const [capRequests, setCapRequests] = useState([]);
  const isMobile = useIsMobile();
  const [awaiting, setAwaiting] = useState([]);
  const [payModal, setPayModal] = useState(null);
  const [schemaWarning, setSchemaWarning] = useState(null);
  const [archiveFor, setArchiveFor] = useState(null);
  const [showStripe, setShowStripe] = useState(false);
  const [stripeInfo, setStripeInfo] = useState(null);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [showDefaults, setShowDefaults] = useState(false);
  const [showRequests, setShowRequests] = useState(false);
  const [showAllAwaiting, setShowAllAwaiting] = useState(false);

  const token = () => localStorage.getItem("admin_token");
  const authHeader = () => ({ Authorization: `Bearer ${token()}` });

  // Is the database up to date with this release? (A skipped migration is the classic cause of
  // "An unexpected error occurred" on newer features.)
  useEffect(() => {
    axios.get(endpoints.health)
      .then((res) => {
        if (res.data.schema_ok === false) {
          setSchemaWarning({ db: res.data.db_version, expected: res.data.expected_db_version });
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    axios.get(endpoints.adminStripeSettings, { headers: authHeader() }).then((res) => setStripeInfo(res.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = () => {
    if (!token()) {
      window.location.href = "/admin/login";
      return;
    }
    loadAwaiting();
    axios.get(endpoints.adminTenants, { headers: authHeader() })
      .then((res) => setTenants(res.data))
      .catch((err) => {
        if (err.response?.status === 401 || err.response?.status === 403) {
          localStorage.removeItem("admin_token");
          window.location.href = "/admin/login";
        }
      });
  };

  const logout = () => {
    localStorage.removeItem("admin_token");
    window.location.href = "/admin/login";
  };

  useEffect(() => {
    load();
    axios.get(endpoints.health).then((res) => setVersion(res.data.version)).catch(() => {});
    axios.get(endpoints.adminDefaultCaps, { headers: authHeader() })
      .then((res) => { setDefaultCaps(res.data); setDefaultCapsForm(res.data); })
      .catch(() => {});
    loadCapRequests();
  }, []);

  const loadAwaiting = () => {
    axios.get(endpoints.adminInvoices("awaiting"), { headers: authHeader() })
      .then((res) => setAwaiting(res.data.invoices || []))
      .catch(() => {});
  };

  const loadCapRequests = () => {
    axios.get(endpoints.adminCapRequests, { headers: authHeader() })
      .then((res) => setCapRequests(res.data))
      .catch(() => {});
  };

  const saveDefaultCaps = async () => {
    let res;
    try {
      res = await axios.patch(endpoints.adminDefaultCaps, defaultCapsForm, { headers: authHeader() });
    } catch (err) {
      window.alert(err.response?.data?.error || "Couldn't save the default caps.");
      return;
    }
    setDefaultCaps(res.data);
    setDefaultCapsForm(res.data);
    setDefaultCapsSaved(true);
    setTimeout(() => setDefaultCapsSaved(false), 2000);
  };

  const cancelDefaultCaps = () => {
    setDefaultCapsForm(defaultCaps);
  };

  const approveCapRequest = async (id) => {
    await axios.post(endpoints.adminApproveCapRequest(id), {}, { headers: authHeader() });
    loadCapRequests();
    load();
  };

  const declineCapRequest = async (id) => {
    await axios.post(endpoints.adminDeclineCapRequest(id), {}, { headers: authHeader() });
    loadCapRequests();
  };

  const approve = async (id) => {
    const res = await axios.post(endpoints.adminApprove(id), {}, { headers: authHeader() });
    alert(res.data.message);
    load();
  };

  const toggleActive = async (id) => {
    await axios.post(endpoints.adminToggleActive(id), {}, { headers: authHeader() });
    load();
  };

  const deleteTenant = async (id, businessName) => {
    if (!window.confirm(`Delete "${businessName}"? This permanently removes their account, staff, menu, tables, and order history. This cannot be undone.`)) {
      return;
    }
    await axios.post(endpoints.adminDelete(id), {}, { headers: authHeader() });
    load();
  };

  const toggleSelect = (id) => {
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const bulkReset = async () => {
    const res = await axios.post(endpoints.adminBulkReset, { tenant_ids: selected }, { headers: authHeader() });
    setSelected([]);
    alert(res.data.message);
    load();
  };

  const openCapsEditor = (t) => {
    setEditingCapsFor(t.id);
    setEditingProfileFor(null);
    setCapsForm({
      max_categories: t.max_categories,
      max_items_per_category: t.max_items_per_category,
      max_tables: t.max_tables,
      max_staff: t.max_staff,
      report_retention_days: t.report_retention_days,
      split_bill_enabled: t.split_bill_enabled,
      max_employees: t.max_employees,
      attendance_enabled: t.attendance_enabled,
      retention_photo_days: t.retention_photo_days,
      retention_hours_days: t.retention_hours_days,
      archive_days: t.archive_days,
      online_ordering_enabled: t.online_ordering_enabled,
      online_pay_store_allowed: t.online_pay_store_allowed,
      online_pay_stripe_allowed: t.online_pay_stripe_allowed,
      stripe_test_mode: t.stripe_test_mode,
      online_retention_days: t.online_retention_days,
    });
  };

  const saveCaps = async (id) => {
    const t = tenants.find((x) => x.id === id);
    const lowering = t && (Number(capsForm.retention_photo_days) < t.retention_photo_days || Number(capsForm.retention_hours_days) < t.retention_hours_days);
    if (lowering && !window.confirm("Lowering a retention limit moves this restaurant's older attendance records to the archive at the next nightly run (they can be restored from Attendance archive). Continue?")) return;
    try {
      // Ensure payment method fields are preserved when saving stripe_test_mode or other changes
      const payloadToSend = {
        ...capsForm,
        online_ordering_enabled: capsForm.online_ordering_enabled !== undefined ? capsForm.online_ordering_enabled : t.online_ordering_enabled,
        online_pay_store_allowed: capsForm.online_pay_store_allowed !== undefined ? capsForm.online_pay_store_allowed : t.online_pay_store_allowed,
        online_pay_stripe_allowed: capsForm.online_pay_stripe_allowed !== undefined ? capsForm.online_pay_stripe_allowed : t.online_pay_stripe_allowed,
      };
      await axios.patch(endpoints.adminCaps(id), payloadToSend, { headers: authHeader() });
    } catch (err) {
      window.alert(err.response?.data?.error || "Couldn't save the caps.");
      return;
    }
    setEditingCapsFor(null);
    load();
  };

  const openProfileEditor = (t) => {
    setEditingProfileFor(t.id);
    setEditingCapsFor(null);
    setProfileForm({
      business_name: t.business_name,
      owner_name: t.owner_name,
      phone_number: t.phone_number || "",
    });
  };

  const saveProfile = async (id) => {
    await axios.patch(endpoints.adminTenantProfile(id), profileForm, { headers: authHeader() });
    setEditingProfileFor(null);
    load();
  };

  const approveEmailChange = async (id) => {
    const res = await axios.post(endpoints.adminApproveEmailChange(id), {}, { headers: authHeader() });
    alert(res.data.message);
    load();
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortDir("asc");
    }
  };

  const sortedTenants = [...tenants].sort((a, b) => {
    if (!sortBy) return 0;
    let av = a[sortBy];
    let bv = b[sortBy];
    if (typeof av === "boolean") { av = av ? 1 : 0; bv = bv ? 1 : 0; }
    if (av == null) av = "";
    if (bv == null) bv = "";
    if (av < bv) return sortDir === "asc" ? -1 : 1;
    if (av > bv) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  const openSubscriptionEditor = (t) => {
    setEditingSubscriptionFor(t.id);
    setEditingCapsFor(null);
    setEditingProfileFor(null);
    setSubscriptionForm({
      subscription_amount: t.subscription_amount || 0,
      is_subscribed: t.is_subscribed,
      subscribed_date: t.subscribed_date ? t.subscribed_date.slice(0, 10) : "",
      subscription_expiration_date: t.subscription_expiration_date ? t.subscription_expiration_date.slice(0, 10) : "",
      autosend_invoice_enabled: t.autosend_invoice_enabled,
    });
  };

  const saveSubscription = async (id) => {
    await axios.patch(endpoints.adminTenantSubscription(id), subscriptionForm, { headers: authHeader() });
    setEditingSubscriptionFor(null);
    load();
  };

  const sendInvoice = async (t) => {
    const body = { tenant_id: t.id };
    if (!t.subscription_amount || t.subscription_amount <= 0) {
      if (!window.confirm(`${t.business_name} has no subscription amount set. Send anyway with a custom amount?`)) return;
      const amt = window.prompt("Invoice amount ($):", "0.00");
      if (amt === null) return;
      body.amount = parseFloat(amt) || 0;
    }
    const post = async (extra) => {
      const res = await axios.post(endpoints.adminSendInvoice, { ...body, ...extra }, { headers: authHeader() });
      alert(res.data.message || "Invoice sent.");
      load();
    };
    try {
      await post({});
    } catch (err) {
      const data = err.response?.data;
      if (err.response?.status === 409 && data?.code === "open_invoice") {
        const detail = data.agreed_count > 0
          ? "The one they agreed to stays open until you mark it paid."
          : "The earlier invoice they haven't agreed to will be replaced.";
        if (window.confirm(`${data.error}\n\nSend another invoice anyway? ${detail}`)) {
          try { await post({ force: true }); } catch (err2) { alert(err2.response?.data?.error || "Could not send the invoice."); }
        }
      } else {
        alert(data?.error || "Could not send the invoice.");
      }
    }
  };

  const bulkAutosend = async (enabled) => {
    const res = await axios.post(
      endpoints.adminBulkAutosendInvoice,
      { tenant_ids: selected, enabled },
      { headers: authHeader() }
    );
    alert(res.data.message);
    load();
  };

  const hasUnpaid = (t) => t.agreed_fee && ["sent", "agreed", "activated"].includes(t.agreed_fee.state);
  const canGrace = (t) => t.agreed_fee && t.agreed_fee.type === "invoice" && ["sent", "agreed"].includes(t.agreed_fee.state);

  // One obvious next step per tenant stays on the row; everything else lives in the ⋯ menu.
  const rowActions = (t) => {
    const all = [];
    if (t.status === "pending") all.push({ key: "approve", label: "Approve", onClick: () => approve(t.id), kind: "go" });
    if (t.status === "approved") {
      all.push({ key: "toggle", label: t.is_active ? "Deactivate" : "Activate", onClick: () => toggleActive(t.id) });
    }
    all.push({ key: "subscription", label: "Edit Subscription", onClick: () => openSubscriptionEditor(t) });
    all.push({ key: "invoice", label: "Send Invoice", onClick: () => sendInvoice(t) });
    if (hasUnpaid(t)) {
      all.push({ key: "paid", label: "Mark Paid", onClick: () => setPayModal({ id: t.agreed_fee.agreement_id, tab: "pay" }), kind: "go" });
    }
    if (canGrace(t)) {
      all.push({ key: "grace", label: "Activate now (unpaid)", onClick: () => setPayModal({ id: t.agreed_fee.agreement_id, tab: "grace" }) });
    }
    all.push({ key: "profile", label: "Edit Profile", onClick: () => openProfileEditor(t) });
    all.push({ key: "caps", label: "Edit Caps", onClick: () => openCapsEditor(t) });
    all.push({ key: "archive", label: "Attendance archive", onClick: () => setArchiveFor(t) });
    all.push({ key: "delete", label: "Delete", onClick: () => deleteTenant(t.id, t.business_name), danger: true });
    const primaryKey = t.status === "pending" ? "approve" : hasUnpaid(t) ? "paid" : "invoice";
    return { primary: all.find((a) => a.key === primaryKey), items: all.filter((a) => a.key !== primaryKey) };
  };

  const actionButtons = (t) => {
    const { primary, items } = rowActions(t);
    return (
      <div style={styles.actionCell}>
        {primary && (
          <button onClick={primary.onClick} style={primary.kind === "go" ? styles.payBtn : styles.actionBtn}>{primary.label}</button>
        )}
        <ActionMenu items={items} label={`More actions for ${t.business_name}`} />
      </div>
    );
  };

  const toggleEmployeeModule = async (t) => {
    const turningOff = t.attendance_enabled;
    const ok = window.confirm(turningOff
      ? `Turn OFF the Employee module for ${t.business_name}? Anyone clocked in will be clocked out and flagged, and clock-in enforcement stops. Recorded hours stay in the system according to its retention settings.`
      : `Turn ON the Employee module for ${t.business_name}?`);
    if (!ok) return;
    await axios.patch(endpoints.adminCaps(t.id), { attendance_enabled: !turningOff }, { headers: authHeader() });
    load();
  };
  const employeePill = (t) => (
    <button
      onClick={() => toggleEmployeeModule(t)}
      aria-label={`Employee module for ${t.business_name}`}
      title="Click to turn the Employee module on or off"
      style={t.attendance_enabled ? styles.pillOn : styles.pillOff}
    >
      Employees {t.attendance_enabled ? `ON ${t.active_employee_count ?? 0}/${t.max_employees}` : "OFF"}
    </button>
  );

  const toggleOnline = async (t) => {
    const off = t.online_ordering_enabled;
    if (!window.confirm(off ? `Turn OFF online ordering for ${t.business_name}? Customers can no longer place orders; existing orders can still be finished.` : `Turn ON online ordering for ${t.business_name}?`)) return;
    try {
      const payload = { online_ordering_enabled: !off };
      // When turning ON, ensure at least one payment method is allowed
      if (!off && !t.online_pay_store_allowed && !t.online_pay_stripe_allowed) {
        payload.online_pay_store_allowed = true;
      }
      await axios.patch(endpoints.adminCaps(t.id), payload, { headers: authHeader() });
    } catch (err) { window.alert(err.response?.data?.error || "Couldn't change that."); }
    load();
  };
  const toggleCard = async (t) => {
    const off = t.online_pay_stripe_allowed;
    if (!window.confirm(off ? `Stop allowing card payments (Stripe) for ${t.business_name}?` : `Allow card payments (Stripe) for ${t.business_name}? They still need to connect their own Stripe account.`)) return;
    try { await axios.patch(endpoints.adminCaps(t.id), { online_pay_stripe_allowed: !off }, { headers: authHeader() }); } catch (err) { window.alert(err.response?.data?.error || "Couldn't change that."); }
    load();
  };
  const onlinePill = (t) => (
    <button onClick={() => toggleOnline(t)} aria-label={`Online ordering for ${t.business_name}`} title="Click to turn online ordering on or off" style={t.online_ordering_enabled ? styles.pillOn : styles.pillOff}>
      Online {t.online_ordering_enabled ? "ON" : "OFF"}
    </button>
  );
  const cardPill = (t) => t.online_ordering_enabled && (
    <button onClick={() => toggleCard(t)} aria-label={`Card payments for ${t.business_name}`} title="Click to allow or stop card payments (Stripe)" style={t.online_pay_stripe_allowed ? styles.pillOn : styles.pillOff}>
      Card {t.online_pay_stripe_allowed ? `ON${t.stripe_test_mode ? " · test" : ""}` : "OFF"}
    </button>
  );

  const closeEditors = () => {
    setEditingProfileFor(null);
    setEditingCapsFor(null);
    setEditingSubscriptionFor(null);
  };

  const matchesFilter = (t, f) => {
    switch (f) {
      case "pending": return t.status === "pending";
      case "active": return t.status === "approved" && t.is_active;
      case "inactive": return t.status === "approved" && !t.is_active;
      case "awaiting": return !!t.agreed_fee && ["agreed", "activated"].includes(t.agreed_fee.state);
      case "subscribed": return !!t.is_subscribed;
      default: return true;
    }
  };
  const needle = query.trim().toLowerCase();
  const filteredTenants = sortedTenants.filter((t) =>
    matchesFilter(t, filter) &&
    (!needle || [t.business_name, t.owner_name, t.email].some((v) => (v || "").toLowerCase().includes(needle)))
  );
  const shownTenants = filteredTenants.slice(0, visible);
  const filterCounts = Object.fromEntries(FILTERS.map((f) => [f.key, tenants.filter((t) => matchesFilter(t, f.key)).length]));
  const allShownSelected = shownTenants.length > 0 && shownTenants.every((t) => selected.includes(t.id));
  const toggleSelectShown = () => {
    if (allShownSelected) setSelected(selected.filter((id) => !shownTenants.some((t) => t.id === id)));
    else setSelected(Array.from(new Set([...selected, ...shownTenants.map((t) => t.id)])));
  };
  const editingId = editingProfileFor || editingCapsFor || editingSubscriptionFor;
  const editingTenant = editingId ? tenants.find((t) => t.id === editingId) : null;
  const editorTitle = editingProfileFor ? "Edit Profile" : editingCapsFor ? "Edit Caps" : "Edit Subscription";

  const editorFor = (t) => {
    if (editingProfileFor === t.id) {
      return (
            <div style={{ display: "flex", gap: space[3], alignItems: "flex-end", flexWrap: "wrap" }}>
              <TextField
                label="Business name"
                value={profileForm.business_name}
                onChange={(v) => setProfileForm({ ...profileForm, business_name: v })}
              />
              <TextField
                label="Owner name"
                value={profileForm.owner_name}
                onChange={(v) => setProfileForm({ ...profileForm, owner_name: v })}
              />
              <TextField
                label="Phone number"
                value={profileForm.phone_number}
                onChange={(v) => setProfileForm({ ...profileForm, phone_number: v })}
              />
              <button onClick={() => saveProfile(t.id)} style={styles.actionBtn}>Save</button>
              <button onClick={() => setEditingProfileFor(null)} style={styles.actionBtn}>Cancel</button>
            </div>
      );
    }
    if (editingCapsFor === t.id) {
      return (
            <div style={{ display: "flex", gap: space[3], alignItems: "flex-end", flexWrap: "wrap" }}>
              <CapField
                label="Max categories"
                value={capsForm.max_categories}
                onChange={(v) => setCapsForm({ ...capsForm, max_categories: v })}
              />
              <CapField
                label="Max items/category"
                value={capsForm.max_items_per_category}
                onChange={(v) => setCapsForm({ ...capsForm, max_items_per_category: v })}
              />
              <CapField
                label="Max tables"
                value={capsForm.max_tables}
                onChange={(v) => setCapsForm({ ...capsForm, max_tables: v })}
              />
              <CapField
                label="Max staff accounts"
                value={capsForm.max_staff}
                onChange={(v) => setCapsForm({ ...capsForm, max_staff: v })}
              />
              <CapField label="Max employees" value={capsForm.max_employees} onChange={(v) => setCapsForm({ ...capsForm, max_employees: v })} />
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <label style={{ fontSize: 11, color: colors.textSecondary }}>Employee module</label>
                <button
                  onClick={() => setCapsForm({ ...capsForm, attendance_enabled: !capsForm.attendance_enabled })}
                  style={{ ...styles.actionBtn, background: capsForm.attendance_enabled ? colors.statusReady : colors.surfaceMuted, color: capsForm.attendance_enabled ? "#fff" : colors.textPrimary }}
                >
                  {capsForm.attendance_enabled ? "On" : "Off"}
                </button>
              </div>
              <CapField label="Photo days (live)" value={capsForm.retention_photo_days} onChange={(v) => setCapsForm({ ...capsForm, retention_photo_days: v })} />
              <CapField label="Hours days (live)" value={capsForm.retention_hours_days} onChange={(v) => setCapsForm({ ...capsForm, retention_hours_days: v })} />
              <CapField label="Archive days (0 = keep)" value={capsForm.archive_days} onChange={(v) => setCapsForm({ ...capsForm, archive_days: v })} />
              <CapToggle label="Online ordering" value={capsForm.online_ordering_enabled} onChange={(v) => setCapsForm({ ...capsForm, online_ordering_enabled: v })} />
              <CapToggle label="Allow pay at store" value={capsForm.online_pay_store_allowed} onChange={(v) => setCapsForm({ ...capsForm, online_pay_store_allowed: v })} />
              <CapToggle label="Allow card (Stripe)" value={capsForm.online_pay_stripe_allowed} onChange={(v) => setCapsForm({ ...capsForm, online_pay_stripe_allowed: v })} />
              <CapToggle label="Stripe TEST mode for this restaurant" value={capsForm.stripe_test_mode} onChange={(v) => setCapsForm({ ...capsForm, stripe_test_mode: v })} />
              <CapField label="Online customer data days" value={capsForm.online_retention_days} onChange={(v) => setCapsForm({ ...capsForm, online_retention_days: v })} />
              <CapField
                label="Report retention (days)"
                value={capsForm.report_retention_days}
                onChange={(v) => setCapsForm({ ...capsForm, report_retention_days: v })}
              />
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <label style={{ fontSize: 11, color: colors.textSecondary }}>Split Bill</label>
                <button
                  onClick={() => setCapsForm({ ...capsForm, split_bill_enabled: !capsForm.split_bill_enabled })}
                  style={{
                    ...styles.actionBtn,
                    background: capsForm.split_bill_enabled ? colors.statusReady : colors.surfaceMuted,
                    color: capsForm.split_bill_enabled ? "#fff" : colors.textPrimary,
                  }}
                >
                  {capsForm.split_bill_enabled ? "On" : "Off"}
                </button>
              </div>
              <button onClick={() => saveCaps(t.id)} style={styles.actionBtn}>Save</button>
              <button onClick={() => setEditingCapsFor(null)} style={styles.actionBtn}>Cancel</button>
            </div>
      );
    }
    if (editingSubscriptionFor === t.id) {
      return (
            <div style={{ display: "flex", gap: space[3], alignItems: "flex-end", flexWrap: "wrap" }}>
              <CapField
                label="Subscription amount ($)"
                value={subscriptionForm.subscription_amount}
                onChange={(v) => setSubscriptionForm({ ...subscriptionForm, subscription_amount: v })}
                step="0.01"
              />
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <label style={{ fontSize: 11, color: colors.textSecondary }}>Subscribed</label>
                <button
                  onClick={() => setSubscriptionForm({ ...subscriptionForm, is_subscribed: !subscriptionForm.is_subscribed })}
                  style={{
                    ...styles.actionBtn,
                    background: subscriptionForm.is_subscribed ? colors.statusReady : colors.surfaceMuted,
                    color: subscriptionForm.is_subscribed ? "#fff" : colors.textPrimary,
                  }}
                >
                  {subscriptionForm.is_subscribed ? "Yes" : "Free"}
                </button>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <label style={{ fontSize: 11, color: colors.textSecondary }}>Subscribed date</label>
                <input
                  type="date"
                  value={subscriptionForm.subscribed_date}
                  onChange={(e) => setSubscriptionForm({ ...subscriptionForm, subscribed_date: e.target.value })}
                  style={{ padding: "6px 8px", borderRadius: radius.sm, border: `1px solid ${colors.border}` }}
                />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <label style={{ fontSize: 11, color: colors.textSecondary }}>Expiration date</label>
                <input
                  type="date"
                  value={subscriptionForm.subscription_expiration_date}
                  onChange={(e) => setSubscriptionForm({ ...subscriptionForm, subscription_expiration_date: e.target.value })}
                  style={{ padding: "6px 8px", borderRadius: radius.sm, border: `1px solid ${colors.border}` }}
                />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <label style={{ fontSize: 11, color: colors.textSecondary }}>Autosend Invoice</label>
                <button
                  onClick={() => setSubscriptionForm({ ...subscriptionForm, autosend_invoice_enabled: !subscriptionForm.autosend_invoice_enabled })}
                  style={{
                    ...styles.actionBtn,
                    background: subscriptionForm.autosend_invoice_enabled ? colors.statusReady : colors.surfaceMuted,
                    color: subscriptionForm.autosend_invoice_enabled ? "#fff" : colors.textPrimary,
                  }}
                >
                  {subscriptionForm.autosend_invoice_enabled ? "On" : "Off"}
                </button>
              </div>
              <button onClick={() => saveSubscription(t.id)} style={styles.actionBtn}>Save</button>
              <button onClick={() => setEditingSubscriptionFor(null)} style={styles.actionBtn}>Cancel</button>
            </div>
      );
    }
    return null;
  };

  const tenantCard = (t) => (
    <div key={t.id} style={styles.tenantCard}>
      <div style={{ display: "flex", gap: space[3], alignItems: "flex-start" }}>
        <input
          type="checkbox"
          aria-label={`Select ${t.business_name}`}
          checked={selected.includes(t.id)}
          onChange={() => toggleSelect(t.id)}
          style={{ marginTop: 4, flexShrink: 0 }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={styles.cardName}>{t.business_name}</div>
          {t.agreed_fee && <PaymentNote fee={t.agreed_fee} />}
          <div style={styles.cardMeta}>{t.owner_name}</div>
          <div style={styles.cardMeta}>{t.email}</div>
          {t.pending_email && (
            <div style={styles.pendingEmailNote}>
              → {t.pending_email}{" "}
              <button onClick={() => approveEmailChange(t.id)} style={styles.approveEmailBtn}>Approve</button>
            </div>
          )}
        </div>
        <span style={{ display: "inline-flex", flexWrap: "wrap", gap: 4, alignItems: "center", maxWidth: "100%" }}><StatusPill status={t.status} />{employeePill(t)}{onlinePill(t)}{cardPill(t)}</span>
      </div>
      <div style={styles.cardFacts}>
        <span style={styles.cardFactLabel}>Access</span><span>{t.is_active ? "Active" : "Inactive"}</span>
        <span style={styles.cardFactLabel}>Subscribed</span>
        <span>{t.is_subscribed ? `Yes ($${Number(t.subscription_amount).toFixed(2)})` : "Free"}</span>
        <span style={styles.cardFactLabel}>Subscribed date</span><span>{fmtDate(t.subscribed_date)}</span>
        <span style={styles.cardFactLabel}>Access ends</span><span>{fmtDate(t.trial_end)}</span>
      </div>
      <div style={styles.cardActions}>{actionButtons(t)}</div>
    </div>
  );

  return (
    <div className="admin-page" style={{ padding: space[6], background: colors.bgPage, minHeight: "100vh", fontFamily: fonts.body }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: space[3] }}>
        <h1 style={pageHeading}>Alvna Remo: Customer Accounts</h1>
        <div style={{ display: "flex", gap: space[2] }}>
          <a href="/admin/dashboard" style={buttonSecondary}>Dashboard</a>
          <button onClick={logout} style={buttonSecondary}>Log Out</button>
        </div>
      </div>

      {schemaWarning && (
        <div style={styles.schemaBanner} role="alert" data-testid="schema-banner">
          <strong>Database update needed.</strong> This version of Alvna Remo expects database v{schemaWarning.expected},
          but yours is at v{schemaWarning.db}. Run the latest migration in phpMyAdmin (see VERSION.txt). Until then some
          features (saving addresses, staff caps, new registrations) will show errors.
        </div>
      )}

      <div style={{ display: "flex", gap: space[2], marginTop: space[4], marginBottom: space[4], flexWrap: "wrap" }}>
        <button
          disabled={selected.length === 0}
          onClick={bulkReset}
          style={{ ...buttonPrimary, opacity: selected.length === 0 ? 0.5 : 1 }}
        >
          Send Password Reset to Selected ({selected.length})
        </button>
        <button
          disabled={selected.length === 0}
          onClick={() => setShowNotify(true)}
          style={{ ...buttonSecondary, opacity: selected.length === 0 ? 0.5 : 1 }}
        >
          Send Notification to Selected ({selected.length})
        </button>
        <button
          disabled={selected.length === 0}
          onClick={() => bulkAutosend(true)}
          style={{ ...buttonSecondary, opacity: selected.length === 0 ? 0.5 : 1 }}
        >
          Turn Autosend Invoice ON for Selected
        </button>
        <button
          disabled={selected.length === 0}
          onClick={() => bulkAutosend(false)}
          style={{ ...buttonSecondary, opacity: selected.length === 0 ? 0.5 : 1 }}
        >
          Turn Autosend Invoice OFF for Selected
        </button>
      </div>

      {awaiting.length > 0 && (
        <div style={{ background: colors.surface, borderRadius: radius.md, border: `1px solid ${colors.border}`, padding: space[4], marginBottom: space[5] }}>
          <h2 style={{ fontFamily: fonts.display, fontSize: 15, margin: "0 0 8px" }}>
            Payments Awaiting <span style={styles.badge}>{awaiting.length}</span>
          </h2>
          {awaiting.slice(0, showAllAwaiting ? awaiting.length : 5).map((inv) => {
            const note = paymentNote({
              type: inv.type, state: inv.state, amount: inv.fee_amount, agreed_at: inv.agreed_at,
              sent_at: inv.created_at, paid_at: inv.paid_at, grace_until: inv.grace_until,
            });
            return (
              <div key={inv.id} style={styles.capRequestRow}>
                <span>
                  <strong>{inv.business_name}</strong>: {note.text}
                  {inv.days_waiting !== null && inv.days_waiting > 0 ? ` (${inv.days_waiting}d)` : ""}
                </span>
                <button onClick={() => setPayModal({ id: inv.id, tab: "pay" })} style={styles.actionBtn}>Review</button>
              </div>
            );
          })}
          {awaiting.length > 5 && (
            <button onClick={() => setShowAllAwaiting(!showAllAwaiting)} style={{ ...styles.actionBtn, marginTop: space[2] }}>
              {showAllAwaiting ? "Show fewer" : `Show all ${awaiting.length}`}
            </button>
          )}
        </div>
      )}

      <div style={styles.strip} data-testid="stripe-strip">
        <span>
          <strong>Stripe (online payments)</strong>{" "}
          <span style={styles.stripSummary}>Platform mode: <strong style={{ color: stripeInfo && stripeInfo.mode === "live" ? "#b42318" : "#1d4ed8" }}>{stripeInfo ? stripeInfo.mode.toUpperCase() : "…"}</strong></span>
        </span>
        <button onClick={() => setShowStripe(true)} style={styles.actionBtn}>Manage</button>
      </div>
      {defaultCapsForm && (
        <div style={styles.strip}>
          <span>
            <strong>Default Caps for New Registrations</strong>{" "}
            <span style={styles.stripSummary}>
              {defaultCapsForm.max_categories} categories · {defaultCapsForm.max_items_per_category} items · {defaultCapsForm.max_tables} tables · {defaultCapsForm.max_staff} staff · {defaultCapsForm.report_retention_days}-day reports · Split Bill {defaultCapsForm.split_bill_enabled ? "On" : "Off"}
            </span>
          </span>
          <button onClick={() => setShowDefaults(!showDefaults)} style={styles.actionBtn}>{showDefaults ? "Hide" : "Edit defaults"}</button>
        </div>
      )}
      {defaultCapsForm && showDefaults && (
        <div style={{ background: colors.surface, borderRadius: radius.md, border: `1px solid ${colors.border}`, padding: space[4], marginBottom: space[5] }}>
          <h2 style={{ fontFamily: fonts.display, fontSize: 15, margin: "0 0 4px" }}>Default Caps for New Registrations</h2>
          <p style={{ fontFamily: fonts.body, fontSize: 12, color: colors.textMuted, marginTop: 0, marginBottom: space[3] }}>
            Applied automatically to every new tenant at registration. Doesn't affect any existing tenant.
            adjust an individual tenant's caps via Edit Caps above instead.
          </p>
          <div style={{ display: "flex", gap: space[3], flexWrap: "wrap", alignItems: "flex-end" }}>
            <CapField label="Max categories" value={defaultCapsForm.max_categories} onChange={(v) => setDefaultCapsForm({ ...defaultCapsForm, max_categories: v })} />
            <CapField label="Max items/category" value={defaultCapsForm.max_items_per_category} onChange={(v) => setDefaultCapsForm({ ...defaultCapsForm, max_items_per_category: v })} />
            <CapField label="Max tables" value={defaultCapsForm.max_tables} onChange={(v) => setDefaultCapsForm({ ...defaultCapsForm, max_tables: v })} />
            <CapField label="Max staff accounts" value={defaultCapsForm.max_staff} onChange={(v) => setDefaultCapsForm({ ...defaultCapsForm, max_staff: v })} />
            <CapField label="Max employees" value={defaultCapsForm.max_employees} onChange={(v) => setDefaultCapsForm({ ...defaultCapsForm, max_employees: v })} />
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label style={{ fontSize: 11, color: colors.textSecondary }}>Employee module</label>
              <button
                onClick={() => setDefaultCapsForm({ ...defaultCapsForm, attendance_enabled: !defaultCapsForm.attendance_enabled })}
                style={{ ...styles.actionBtn, background: defaultCapsForm.attendance_enabled ? colors.statusReady : colors.surfaceMuted, color: defaultCapsForm.attendance_enabled ? "#fff" : colors.textPrimary }}
              >
                {defaultCapsForm.attendance_enabled ? "On" : "Off"}
              </button>
            </div>
            <CapField label="Photo days (live)" value={defaultCapsForm.retention_photo_days} onChange={(v) => setDefaultCapsForm({ ...defaultCapsForm, retention_photo_days: v })} />
            <CapField label="Hours days (live)" value={defaultCapsForm.retention_hours_days} onChange={(v) => setDefaultCapsForm({ ...defaultCapsForm, retention_hours_days: v })} />
            <CapField label="Archive days (0 = keep)" value={defaultCapsForm.archive_days} onChange={(v) => setDefaultCapsForm({ ...defaultCapsForm, archive_days: v })} />
            <CapToggle label="Online ordering" value={defaultCapsForm.online_ordering_enabled} onChange={(v) => setDefaultCapsForm({ ...defaultCapsForm, online_ordering_enabled: v })} />
            <CapToggle label="Allow pay at store" value={defaultCapsForm.online_pay_store_allowed} onChange={(v) => setDefaultCapsForm({ ...defaultCapsForm, online_pay_store_allowed: v })} />
            <CapToggle label="Allow card (Stripe)" value={defaultCapsForm.online_pay_stripe_allowed} onChange={(v) => setDefaultCapsForm({ ...defaultCapsForm, online_pay_stripe_allowed: v })} />
            <CapField label="Online customer data days" value={defaultCapsForm.online_retention_days} onChange={(v) => setDefaultCapsForm({ ...defaultCapsForm, online_retention_days: v })} />
            <CapField label="Report retention (days)" value={defaultCapsForm.report_retention_days} onChange={(v) => setDefaultCapsForm({ ...defaultCapsForm, report_retention_days: v })} />
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label style={{ fontSize: 11, color: colors.textSecondary }}>Split Bill</label>
              <button
                onClick={() => setDefaultCapsForm({ ...defaultCapsForm, split_bill_enabled: !defaultCapsForm.split_bill_enabled })}
                style={{
                  ...styles.actionBtn,
                  background: defaultCapsForm.split_bill_enabled ? colors.statusReady : colors.surfaceMuted,
                  color: defaultCapsForm.split_bill_enabled ? "#fff" : colors.textPrimary,
                }}
              >
                {defaultCapsForm.split_bill_enabled ? "On" : "Off"}
              </button>
            </div>
            <button onClick={saveDefaultCaps} style={buttonPrimary}>Save</button>
            <button onClick={cancelDefaultCaps} style={buttonSecondary}>Cancel</button>
            {defaultCapsSaved && <span style={{ fontFamily: fonts.body, fontSize: 13, color: colors.statusReady, fontWeight: 600 }}>✓ Saved</span>}
          </div>
        </div>
      )}

      {capRequests.length > 0 && (
        <div style={styles.strip}>
          <span>
            <strong>Pending Limit Requests</strong> <span style={styles.badge}>{capRequests.length}</span>
          </span>
          <button onClick={() => setShowRequests(!showRequests)} style={styles.actionBtn}>{showRequests ? "Hide" : "Review"}</button>
        </div>
      )}
      {capRequests.length > 0 && showRequests && (
        <div style={{ background: colors.surface, borderRadius: radius.md, border: `1px solid ${colors.border}`, padding: space[4], marginBottom: space[5] }}>
          <h2 style={{ fontFamily: fonts.display, fontSize: 15, margin: "0 0 8px" }}>Pending Limit Requests</h2>
          {capRequests.map((r) => (
            <div key={r.id} style={styles.capRequestRow}>
              <span>
                <strong>{r.business_name}</strong> wants {FIELD_LABELS[r.field_name] || r.field_name}:{" "}
                {["split_bill_enabled", "attendance_enabled", "online_ordering_enabled", "online_pay_stripe_allowed"].includes(r.field_name) ? (r.requested_value === "1" ? "Turn On" : "Turn Off") : r.requested_value}
              </span>
              <div style={{ display: "flex", gap: 6 }}>
                <button onClick={() => approveCapRequest(r.id)} style={styles.actionBtn}>Approve</button>
                <button onClick={() => declineCapRequest(r.id)} style={styles.deleteBtn}>Decline</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={styles.filterBar}>
        <input
          aria-label="Search tenants"
          placeholder="Search business, owner or email…"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setVisible(PAGE_SIZE); }}
          style={{ ...inputBase, flex: "1 1 240px", minWidth: 0 }}
        />
        <select
          id="tenant-sort"
          aria-label="Sort tenants"
          value={sortBy || ""}
          onChange={(e) => { setSortBy(e.target.value || null); setSortDir("asc"); }}
          style={{ ...inputBase, width: "auto" }}
        >
          <option value="">Newest first</option>
          <option value="business_name">Business</option>
          <option value="status">Status</option>
          <option value="is_subscribed">Subscribed</option>
          <option value="subscribed_date">Subscribed date</option>
          <option value="trial_end">Access ends</option>
        </select>
        <button onClick={() => setSortDir(sortDir === "asc" ? "desc" : "asc")} style={buttonSecondary} aria-label="Toggle sort direction">
          {sortDir === "asc" ? "▲" : "▼"}
        </button>
      </div>
      <div style={styles.chips} role="group" aria-label="Filter tenants">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => { setFilter(f.key); setVisible(PAGE_SIZE); }}
            aria-pressed={filter === f.key}
            style={filter === f.key ? styles.chipActive : styles.chip}
          >
            {f.label} <span style={{ opacity: 0.75 }}>{filterCounts[f.key]}</span>
          </button>
        ))}
      </div>

      {filteredTenants.length === 0 ? (
        <p style={{ color: colors.textSecondary }}>No tenants match.</p>
      ) : isMobile ? (
        <div>{shownTenants.map((t) => tenantCard(t))}</div>
      ) : (
        <div style={{ background: colors.surface, borderRadius: radius.md, border: `1px solid ${colors.border}`, overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ textAlign: "left", borderBottom: `1px solid ${colors.border}` }}>
                <th style={styles.th}>
                  <input type="checkbox" aria-label="Select all shown" checked={allShownSelected} onChange={toggleSelectShown} />
                </th>
                <th style={styles.thSortable} onClick={() => toggleSort("business_name")}>Business {sortBy === "business_name" ? (sortDir === "asc" ? "▲" : "▼") : ""}</th>
                <th style={styles.th}>Contact</th>
                <th style={styles.thSortable} onClick={() => toggleSort("status")}>Status {sortBy === "status" ? (sortDir === "asc" ? "▲" : "▼") : ""}</th>
                <th style={styles.thSortable} onClick={() => toggleSort("is_subscribed")}>Subscription {sortBy === "is_subscribed" ? (sortDir === "asc" ? "▲" : "▼") : ""}</th>
                <th style={styles.thSortable} onClick={() => toggleSort("trial_end")}>Access Ends {sortBy === "trial_end" ? (sortDir === "asc" ? "▲" : "▼") : ""}</th>
                <th style={styles.th}></th>
              </tr>
            </thead>
            <tbody>
              {shownTenants.map((t) => (
                <tr key={t.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                  <td style={styles.td}>
                    <input type="checkbox" checked={selected.includes(t.id)} onChange={() => toggleSelect(t.id)} aria-label={`Select ${t.business_name}`} />
                  </td>
                  <td style={styles.td}>
                    <div style={{ fontWeight: 600, color: colors.textPrimary }}>{t.business_name}</div>
                    {t.agreed_fee && <PaymentNote fee={t.agreed_fee} />}
                  </td>
                  <td style={styles.td}>
                    <div>{t.owner_name}</div>
                    <div style={styles.cellSub}>{t.email}</div>
                    {t.pending_email && (
                      <div style={styles.pendingEmailNote}>
                        → {t.pending_email}{" "}
                        <button onClick={() => approveEmailChange(t.id)} style={styles.approveEmailBtn}>Approve</button>
                      </div>
                    )}
                  </td>
                  <td style={styles.td}>
                    <StatusPill status={t.status} />
                    {t.status === "approved" && !t.is_active && <div style={styles.cellSub}>Inactive</div>}
                    <div style={{ marginTop: 4, display: "flex", gap: 4, flexWrap: "wrap" }}>{employeePill(t)}{onlinePill(t)}{cardPill(t)}</div>
                  </td>
                  <td style={styles.td}>
                    <div>{t.is_subscribed ? `Yes ($${Number(t.subscription_amount).toFixed(2)})` : "Free"}</div>
                    {t.subscribed_date && <div style={styles.cellSub}>since {fmtDate(t.subscribed_date)}</div>}
                  </td>
                  <td style={styles.td}>{fmtDate(t.trial_end)}</td>
                  <td style={{ ...styles.td, textAlign: "right", whiteSpace: "nowrap" }}>{actionButtons(t)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {filteredTenants.length > 0 && (
        <div style={styles.pager}>
          <span>Showing {shownTenants.length} of {filteredTenants.length}{filteredTenants.length !== tenants.length ? ` (${tenants.length} total)` : ""}</span>
          {filteredTenants.length > shownTenants.length && (
            <button onClick={() => setVisible(visible + PAGE_SIZE)} style={styles.actionBtn}>Show more</button>
          )}
        </div>
      )}

      {version && (
        <div style={{ marginTop: space[5], fontSize: 11, color: colors.textMuted }}>
          Alvna Remo v{version}
        </div>
      )}

      {showStripe && (
        <EditorDrawer title="Stripe (online payments)" onClose={() => setShowStripe(false)}>
          <StripePanel authHeader={authHeader} info={stripeInfo} onChanged={setStripeInfo} />
        </EditorDrawer>
      )}

      {archiveFor && (
        <EditorDrawer title={`Attendance archive: ${archiveFor.business_name}`} onClose={() => setArchiveFor(null)}>
          <ArchivePanel tenant={archiveFor} authHeader={authHeader} />
        </EditorDrawer>
      )}

      {editingTenant && (
        <EditorDrawer title={`${editorTitle}: ${editingTenant.business_name}`} onClose={closeEditors}>
          {editorFor(editingTenant)}
        </EditorDrawer>
      )}

      {payModal && (
        <PaymentModal
          key={payModal.id}
          invoiceId={payModal.id}
          initialTab={payModal.tab}
          authHeader={authHeader}
          onClose={() => setPayModal(null)}
          onChanged={() => { load(); loadCapRequests(); }}
        />
      )}

      {showNotify && (
        <NotifyModal
          selected={selected}
          tenants={tenants}
          capRequests={capRequests}
          authHeader={authHeader}
          onClose={() => setShowNotify(false)}
          onSent={() => { setShowNotify(false); setSelected([]); load(); loadCapRequests(); }}
        />
      )}
    </div>
  );
}

const TEMPLATES = [
  {
    id: "blank",
    label: "General / Blank",
    subject: "",
    body: "",
  },
  {
    id: "fee_required",
    label: "Fee Required for Cap Increase",
    subject: "A quick note about your limit increase request",
    body:
      "Hi there,\n\nThanks for your request to increase your account limits. " +
      "Here's the subscription breakdown:\n\n{{CAP_FEE_TABLE}}\n\n" +
      "Please confirm you agree to this subscription fee using the link below, and we'll " +
      "follow up separately to arrange payment:\n\n{{AGREE_LINK}}\n\n" +
      "Thanks,\nAlvna",
  },
  {
    id: "cap_approved_no_fee",
    label: "Cap Increase Approved (No Fee)",
    subject: "Your limit increase has been approved",
    body: "Hi there,\n\nGood news, your requested limit increase has been approved, no fee required. You should see the change reflected in your account now.\n\nThanks,\nAlvna",
  },
  {
    id: "cap_declined",
    label: "Cap Increase Declined",
    subject: "About your limit increase request",
    body: "Hi there,\n\nThanks for reaching out about increasing your account limits. We're not able to accommodate this request at the moment. Feel free to reply if you'd like to discuss further.\n\nThanks,\nAlvna",
  },
  {
    id: "welcome",
    label: "Welcome / Account Approved",
    subject: "Welcome to Alvna Remo!",
    body: "Hi there,\n\nWelcome aboard! Your account is approved and ready to use. Let us know if you have any questions getting started.\n\nIf the Employee module is on for your account, you'll also find Employees and Attendance in the side menu. If Online Ordering is on, you'll find that there too, plus an Online orders tab on the Kitchen and Waiter screens.\n\nThanks,\nAlvna",
  },
  {
    id: "employee_on",
    label: "Employee Module Enabled",
    subject: "The Employee module is now on for your restaurant",
    body: "Hi there,\n\nGood news, the Employee module is now turned on for your restaurant. It lets your team clock in and out from their phones with a photo and a GPS check, keeps their hours, and exports them for payroll.\n\nTo get started: open Employees in your menu, add your people with a 4–6 digit PIN each, and share the clock-in link or print its QR code. Then open Attendance to set your rules (GPS check, and whether linked staff must be clocked in). Make sure your restaurant location is set under Settings first.\n\nLet us know if you have any questions.\n\nThanks,\nAlvna",
  },
  {
    id: "online_on",
    label: "Online Ordering Enabled",
    subject: "Online ordering is now on for your restaurant",
    body: "Hi there,\n\nGood news, Online Ordering is now turned on for your restaurant. Customers can order for pickup from your menu, ASAP or at a time they choose, and track their order by order number or phone number.\n\nTo get started: open Online Ordering in your menu, set your hours, prep time and how customers pay, then share your ordering link or print its QR code. New orders appear on the Kitchen and Waiter screens in the Online orders tab (Pending payment first, then the kitchen).\n\nLet us know if you have any questions.\n\nThanks,\nAlvna",
  },
  {
    id: "online_off",
    label: "Online Ordering Turned Off",
    subject: "Online ordering has been turned off",
    body: "Hi there,\n\nOnline Ordering has been turned off for your restaurant, so customers can no longer place new online orders. Orders already placed can still be finished from the Online orders tab, and customers can still track them.\n\nLet us know if you have any questions.\n\nThanks,\nAlvna",
  },
  {
    id: "card_on",
    label: "Online Card Payments Enabled",
    subject: "Online card payments are now available for your restaurant",
    body: "Hi there,\n\nOnline card payments (Stripe) are now available for your restaurant. To use them: open Online Ordering, choose “Card online (Stripe)”, and press Connect Stripe to link your own Stripe account (a few minutes, Stripe will ask for your business and bank details). Payments go straight into your Stripe account, and paid orders go to the kitchen automatically.\n\nWe suggest a test order first. Stripe's test cards work while your account is in test mode.\n\nLet us know if you have any questions.\n\nThanks,\nAlvna",
  },
  {
    id: "card_off",
    label: "Online Card Payments Turned Off",
    subject: "Online card payments have been turned off",
    body: "Hi there,\n\nOnline card payments have been turned off for your restaurant. Customers can no longer pay by card when ordering online; if your restaurant is set to “Pay at the store”, ordering continues as normal. Card orders already paid can still be finished or refunded from the Online orders tab.\n\nLet us know if you have any questions.\n\nThanks,\nAlvna",
  },
  {
    id: "employee_off",
    label: "Employee Module Turned Off",
    subject: "The Employee module has been turned off",
    body: "Hi there,\n\nThe Employee module has been turned off for your restaurant. Anyone still clocked in was clocked out automatically and flagged, and staff accounts are no longer restricted by clock-in. Your recorded hours are kept according to your retention settings, in case the module is turned back on.\n\nLet us know if you have any questions.\n\nThanks,\nAlvna",
  },
  {
    id: "reminder",
    label: "Trial/Account Reminder",
    subject: "A quick check-in from Alvna",
    body: "Hi there,\n\nJust checking in to see how things are going with your account. Let us know if there's anything we can help with.\n\nThanks,\nAlvna",
  },
  {
    id: "action_needed",
    label: "Account Issue / Action Needed",
    subject: "Action needed on your Alvna Remo account",
    body: "Hi there,\n\nWe noticed something on your account that needs your attention. Please reply to this email or reach out to contact@alvna.com so we can help resolve it.\n\nThanks,\nAlvna",
  },
];

function CapToggle({ label, value, onChange }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={{ fontSize: 11, color: colors.textSecondary }}>{label}</label>
      <button
        onClick={() => onChange(!value)}
        style={{ ...styles.actionBtn, background: value ? colors.statusReady : colors.surfaceMuted, color: value ? "#fff" : colors.textPrimary }}
      >
        {value ? "On" : "Off"}
      </button>
    </div>
  );
}

// Platform-wide Stripe settings. The secret keys themselves live in config.php on the server, never here.
function StripePanel({ authHeader, info, onChanged }) {
  const [fee, setFee] = useState(info ? String(info.application_fee_percent) : "0");
  const [msg, setMsg] = useState(null);
  const [test, setTest] = useState({});
  const url = endpoints.adminStripeSettings;
  const err = (e, fb) => setMsg({ ok: false, text: e.response?.data?.error || fb });
  const setMode = async (mode) => {
    setMsg(null);
    if (mode === "live" && !window.confirm("Switch the WHOLE platform to LIVE? Restaurants that are not flagged as test will charge real cards.")) return;
    try { const r = await axios.patch(url, { mode }, { headers: authHeader() }); onChanged(r.data); setMsg({ ok: true, text: `Platform is now in ${mode.toUpperCase()} mode.` }); } catch (e) { err(e, "Couldn't switch."); }
  };
  const saveFee = async () => {
    setMsg(null);
    try { const r = await axios.patch(url, { application_fee_percent: Number(fee) }, { headers: authHeader() }); onChanged(r.data); setMsg({ ok: true, text: "Platform fee saved." }); } catch (e) { err(e, "Couldn't save."); }
  };
  const testConn = async (mode) => {
    setTest((t) => ({ ...t, [mode]: "Testing…" }));
    try {
      const r = await axios.post(url, { action: "test_connection", mode }, { headers: authHeader() });
      setTest((t) => ({ ...t, [mode]: r.data.ok ? `✓ Connected to ${r.data.name || r.data.account}` : `✗ ${r.data.error}` }));
    } catch (e) { setTest((t) => ({ ...t, [mode]: "✗ Couldn't test" })); }
  };
  if (!info) return <p style={{ fontFamily: fonts.body }}>Loading…</p>;
  const Row = ({ label, ok }) => <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}><span>{label}</span><strong style={{ color: ok ? "#067647" : "#b42318" }}>{ok ? "configured" : "missing"}</strong></div>;
  return (
    <div style={{ fontFamily: fonts.body, fontSize: 13, color: colors.textPrimary }} data-testid="stripe-panel">
      <div style={{ fontWeight: 600, marginBottom: 4 }}>Platform mode</div>
      <div style={{ display: "flex", gap: 6 }}>
        <button style={{ ...styles.actionBtn, ...(info.mode === "test" ? { background: colors.brandBlue, color: "#fff" } : {}) }} onClick={() => setMode("test")}>Test</button>
        <button style={{ ...styles.actionBtn, ...(info.mode === "live" ? { background: "#b42318", color: "#fff" } : {}) }} onClick={() => setMode("live")}>Live</button>
      </div>
      <p style={{ color: colors.textMuted, fontSize: 12 }}>In Test mode every restaurant uses Stripe test keys. In Live mode a restaurant can still be flagged “Stripe TEST mode” under Edit Caps, to test safely while others are live.</p>
      <div style={{ fontWeight: 600, margin: "10px 0 4px" }}>Keys on the server (config.php)</div>
      <Row label="Test secret key" ok={info.keys.test} /><Row label="Live secret key" ok={info.keys.live} />
      <Row label="Test webhook secret" ok={info.webhook_secrets.test} /><Row label="Live webhook secret" ok={info.webhook_secrets.live} />
      <div style={{ fontWeight: 600, margin: "10px 0 4px" }}>Webhook address (add it in Stripe, including Connect events)</div>
      <input readOnly value={info.webhook_url} style={{ width: "100%", boxSizing: "border-box", padding: 8, fontSize: 12, border: `1px solid ${colors.border}`, borderRadius: radius.sm }} onFocus={(e) => e.target.select()} aria-label="Webhook address" />
      <div style={{ color: colors.textMuted, fontSize: 12, margin: "4px 0" }}>Events: checkout.session.completed, checkout.session.expired, checkout.session.async_payment_succeeded, checkout.session.async_payment_failed, account.updated, charge.refunded.</div>
      <div style={{ fontWeight: 600, margin: "10px 0 4px" }}>Alvna platform fee on online payments</div>
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <input type="number" min="0" max="20" step="0.1" value={fee} onChange={(e) => setFee(e.target.value)} style={{ width: 80, padding: 6, border: `1px solid ${colors.border}`, borderRadius: radius.sm }} aria-label="Platform fee percent" /> %
        <button style={styles.actionBtn} onClick={saveFee}>Save fee</button>
      </div>
      <div style={{ fontWeight: 600, margin: "10px 0 4px" }}>Test connection</div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        <button style={styles.actionBtn} onClick={() => testConn("test")}>Test key</button>
        <button style={styles.actionBtn} onClick={() => testConn("live")}>Live key</button>
      </div>
      {["test", "live"].map((m) => test[m] && <div key={m} style={{ marginTop: 4 }} data-testid={`stripe-test-${m}`}>{m}: {test[m]}</div>)}
      {msg && <div role="status" style={{ marginTop: 10, padding: 8, borderRadius: radius.sm, background: msg.ok ? "#ecfdf3" : "#fef3f2", color: msg.ok ? "#067647" : "#b42318" }}>{msg.text}</div>}
    </div>
  );
}

function ArchivePanel({ tenant, authHeader }) {
  const [info, setInfo] = useState(null);
  const [range, setRange] = useState({ from: "", to: "" });
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState("");
  const [file, setFile] = useState(null);
  const url = (qs) => endpoints.adminAttendanceArchive(tenant.id, qs);

  const load = () => axios.get(url(""), { headers: authHeader() }).then((res) => setInfo(res.data)).catch(() => setMsg({ ok: false, text: "Couldn't load the archive." }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const errText = async (err, fallback) => {
    try { if (err.response?.data instanceof Blob) return JSON.parse(await err.response.data.text()).error || fallback; } catch (e) { /* fall through */ }
    return err.response?.data?.error || fallback;
  };
  const needRange = () => {
    if (!range.from || !range.to) { setMsg({ ok: false, text: "Choose a From and To date first (or click a month above)." }); return false; }
    return true;
  };
  const pickMonth = (m) => {
    const [y, mo] = m.split("-").map(Number);
    const last = new Date(y, mo, 0).getDate();
    setRange({ from: `${m}-01`, to: `${m}-${String(last).padStart(2, "0")}` });
  };
  const summary = (r) => Object.entries(r).filter(([, v]) => v > 0).map(([k, v]) => `${v} ${k.replace(/_/g, " ")}`).join(", ") || "nothing to do";

  const download = async () => {
    if (!needRange()) return;
    setBusy("download"); setMsg(null);
    try {
      const res = await axios.get(url(`&action=download&from=${range.from}&to=${range.to}`), { headers: authHeader(), responseType: "blob" });
      const a = document.createElement("a");
      const href = URL.createObjectURL(res.data);
      a.href = href; a.download = `attendance_archive_${tenant.business_name.replace(/[^A-Za-z0-9]+/g, "_")}_${range.from}_to_${range.to}.zip`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(href);
      setMsg({ ok: true, text: "Downloaded. Keep the ZIP somewhere safe. It contains names, locations and photos." });
    } catch (err) { setMsg({ ok: false, text: await errText(err, "Couldn't download.") }); }
    setBusy("");
  };
  const post = async (action, extra, confirmText, verb) => {
    if (!needRange()) return;
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(action); setMsg(null);
    try {
      const res = await axios.post(url(""), { tenant: tenant.id, action, from: range.from, to: range.to, ...extra }, { headers: authHeader() });
      setMsg({ ok: true, text: `${verb}: ${summary(res.data)}.` });
      load();
    } catch (err) { setMsg({ ok: false, text: await errText(err, "That didn't work.") }); }
    setBusy("");
  };
  const restoreZip = async () => {
    if (!file) { setMsg({ ok: false, text: "Choose the ZIP file first." }); return; }
    setBusy("zip"); setMsg(null);
    const fd = new FormData();
    fd.append("tenant", tenant.id); fd.append("action", "restore_zip"); fd.append("file", file);
    try {
      const res = await axios.post(url(""), fd, { headers: authHeader() });
      setMsg({ ok: true, text: `Restored from ZIP: ${summary(res.data)}.` });
      setFile(null); load();
    } catch (err) { setMsg({ ok: false, text: await errText(err, "Couldn't restore that file.") }); }
    setBusy("");
  };

  if (!info) return <p style={{ fontFamily: fonts.body, color: colors.textSecondary }}>Loading…</p>;
  const r = info.retention;
  return (
    <div style={{ fontFamily: fonts.body, fontSize: 13, color: colors.textPrimary }} data-testid="archive-panel">
      <p style={{ marginTop: 0, color: colors.textSecondary }}>
        Live: photos {r.photo_days} days, hours {r.hours_days} days. Older records move here, out of the restaurant's view
        {r.archive_days > 0 ? ` and are permanently deleted ${r.archive_days} days after archiving.` : " and are kept until you delete them."}
      </p>
      <div style={styles.archiveStats}>
        <span>Live shifts: <strong>{info.live.shifts}</strong> ({info.live.photos} photos)</span>
        <span>Archived: <strong>{info.archived.shifts}</strong> shifts, <strong>{info.archived.photos}</strong> photos</span>
      </div>
      {info.archived.months.length > 0 && (
        <table style={{ width: "100%", borderCollapse: "collapse", margin: "8px 0" }}>
          <thead><tr style={{ textAlign: "left", color: colors.textSecondary }}><th style={styles.th}>Month</th><th style={styles.th}>Shifts</th><th style={styles.th}>Photos</th><th style={styles.th} /></tr></thead>
          <tbody>
            {info.archived.months.map((m) => (
              <tr key={m.month} style={{ borderTop: `1px solid ${colors.border}` }}>
                <td style={styles.td}>{m.month}</td><td style={styles.td}>{m.shifts}</td><td style={styles.td}>{m.photos}</td>
                <td style={{ ...styles.td, textAlign: "right" }}><button style={styles.actionBtn} onClick={() => pickMonth(m.month)}>Use this month</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {info.archived.months.length === 0 && <p style={{ color: colors.textMuted }}>Nothing is archived yet.</p>}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "8px 0" }}>
        <label style={styles.archiveField}>From <input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} style={styles.archiveInput} /></label>
        <label style={styles.archiveField}>To <input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} style={styles.archiveInput} /></label>
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        <button style={styles.actionBtn} disabled={!!busy} onClick={download}>Download ZIP backup</button>
        <button style={styles.actionBtn} disabled={!!busy} onClick={() => post("restore", {}, "Put these archived records back in the restaurant's live view? They stay protected from re-archiving for 14 days.", "Restored")}>Restore to restaurant</button>
        <button style={{ ...styles.actionBtn, color: "#b91c1c" }} disabled={!!busy} onClick={() => post("purge", { confirm: true }, "PERMANENTLY delete these archived records from the server? Download a ZIP backup first. This cannot be undone.", "Deleted")}>Delete from server</button>
      </div>
      <p style={{ color: colors.textMuted, fontSize: 12 }}>The ZIP covers up to about 3 months at a time. Download it first, then delete from the server to free space; you can restore it later from the ZIP.</p>
      <div style={{ borderTop: `1px solid ${colors.border}`, paddingTop: 10, marginTop: 6 }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Restore from a ZIP file</div>
        <input type="file" accept=".zip,application/zip" onChange={(e) => setFile(e.target.files[0] || null)} aria-label="ZIP file to restore" />
        <button style={{ ...styles.actionBtn, marginLeft: 6 }} disabled={!!busy} onClick={restoreZip}>Restore from ZIP</button>
        {info.max_upload_bytes && <p style={{ color: colors.textMuted, fontSize: 12 }}>Your server accepts uploads up to {(info.max_upload_bytes / 1048576).toFixed(0)} MB. Restore a shorter date range if a ZIP is bigger.</p>}
      </div>
      {msg && <div role="status" style={{ marginTop: 10, padding: 8, borderRadius: radius.sm, background: msg.ok ? "#ecfdf3" : "#fef3f2", color: msg.ok ? "#067647" : "#b42318" }}>{msg.text}</div>}
    </div>
  );
}

const PAGE_SIZE = 25;
const FILTERS = [
  { key: "all", label: "All" },
  { key: "pending", label: "Pending approval" },
  { key: "active", label: "Active" },
  { key: "inactive", label: "Inactive" },
  { key: "awaiting", label: "Awaiting payment" },
  { key: "subscribed", label: "Subscribed" },
];

// The "⋯ More" menu. Positioned with position:fixed so a table's scroll box can never clip it,
// and flipped upward when there isn't room below.
function ActionMenu({ items, label }) {
  const [pos, setPos] = useState(null);
  const wrapRef = useRef(null);
  const btnRef = useRef(null);
  const open = pos !== null;

  // Where the menu goes: under the ⋯ button, flipped above it when there isn't room below.
  const place = () => {
    const r = btnRef.current.getBoundingClientRect();
    const height = items.length * 38 + 12;
    const width = 200;
    const top = window.innerHeight - r.bottom < height + 8 ? Math.max(8, r.top - height - 4) : r.bottom + 4;
    return { top, left: Math.max(8, Math.min(window.innerWidth - width - 8, r.right - width)) };
  };

  useEffect(() => {
    if (!open) return undefined;
    const close = () => setPos(null);
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) close(); };
    const onKey = (e) => { if (e.key === "Escape") close(); };
    // The menu is position:fixed (a table's scroll box can't clip it), so keep it attached to its
    // button while the page scrolls or the layout shifts, and close it once the button is off screen.
    const follow = () => {
      if (!btnRef.current) return;
      const r = btnRef.current.getBoundingClientRect();
      if (r.bottom < 0 || r.top > window.innerHeight) close();
      else setPos(place());
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", follow, true);
    window.addEventListener("resize", follow);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", follow, true);
      window.removeEventListener("resize", follow);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggle = () => {
    if (open) { setPos(null); return; }
    setPos(place());
  };

  return (
    <span ref={wrapRef} style={{ display: "inline-block" }}>
      <button ref={btnRef} aria-label={label} aria-haspopup="menu" aria-expanded={open} onClick={toggle} style={styles.moreBtn}>⋯</button>
      {open && (
        <div role="menu" style={{ ...styles.menu, top: pos.top, left: pos.left }}>
          {items.map((it) => (
            <button
              key={it.key}
              role="menuitem"
              onClick={() => { setPos(null); it.onClick(); }}
              style={{ ...styles.menuItem, ...(it.danger ? { color: "#b91c1c" } : {}) }}
            >
              {it.label}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}

// Side panel for the Edit Subscription / Profile / Caps forms, so opening one no longer
// pushes the tenant list down the page.
function EditorDrawer({ title, onClose, children }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <>
      <div style={styles.drawerBackdrop} onClick={onClose} data-testid="editor-backdrop" />
      <aside style={styles.drawer} role="dialog" aria-label={title}>
        <div style={styles.drawerHeader}>
          <strong style={{ fontFamily: fonts.display, fontSize: 15, minWidth: 0, wordBreak: "break-word" }}>{title}</strong>
          <button onClick={onClose} aria-label="Close editor" style={styles.actionBtn}>Close</button>
        </div>
        <div style={{ padding: space[4] }}>{children}</div>
      </aside>
    </>
  );
}

const CAP_FEE_ROWS = [
  { label: "Max Categories" },
  { label: "Max Items per Category" },
  { label: "Max Tables" },
  { label: "Max Staff Accounts" },
  { label: "Max Employees" },
  { label: "Employee Module" },
  { label: "Online Ordering" },
  { label: "Online Card Payments (Stripe)" },
  { label: "Report Retention" },
  { label: "Split Bill" },
];

function NotifyModal({ selected, tenants, capRequests, authHeader, onClose, onSent }) {
  const [templateId, setTemplateId] = useState("blank");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [capFees, setCapFees] = useState(CAP_FEE_ROWS.map(() => "0.00"));
  const [selectedCapRequestId, setSelectedCapRequestId] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const isFeeTemplate = templateId === "fee_required";
  const singleTenant = selected.length === 1 ? tenants.find((t) => t.id === selected[0]) : null;
  const tenantCapRequests = singleTenant
    ? capRequests.filter((r) => r.tenant_public_id === singleTenant.id)
    : [];
  const feeTotal = capFees.reduce((sum, v) => sum + (parseFloat(v) || 0), 0);

  const applyTemplate = (id) => {
    setTemplateId(id);
    const tpl = TEMPLATES.find((t) => t.id === id);
    setSubject(tpl.subject);
    setMessage(tpl.body);
  };

  const send = async () => {
    setError("");

    if (isFeeTemplate) {
      if (selected.length !== 1) {
        setError("Select exactly one tenant to send a subscription fee request.");
        return;
      }
      setSending(true);
      try {
        const res = await axios.post(
          endpoints.adminSendFeeRequest,
          {
            tenant_id: selected[0],
            cap_request_id: selectedCapRequestId || null,
            cap_fees: CAP_FEE_ROWS.map((row, i) => ({ label: row.label, amount: parseFloat(capFees[i]) || 0 })),
            subject,
            message,
          },
          { headers: authHeader() }
        );
        alert(res.data.message);
        onSent();
      } catch (err) {
        setError(err.response?.data?.error || "Could not send fee request.");
      } finally {
        setSending(false);
      }
      return;
    }

    setSending(true);
    try {
      const res = await axios.post(
        endpoints.adminSendNotification,
        { tenant_ids: selected, subject, message },
        { headers: authHeader() }
      );
      alert(res.data.message);
      onSent();
    } catch (err) {
      setError(err.response?.data?.error || "Could not send notification.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ fontFamily: fonts.display, margin: "0 0 12px" }}>
          Send Notification to {selected.length} Tenant(s)
        </h3>
        <p style={{ fontFamily: fonts.body, fontSize: 12, color: colors.textMuted, marginTop: 0 }}>
          Sent as a real email. Any reply the tenant sends lands in your normal mailbox,
          not back in this app.
        </p>
        {error && <div style={styles.errorMsg}>{error}</div>}

        <select
          style={{ ...inputBase, width: "100%", marginBottom: space[2], boxSizing: "border-box" }}
          value={templateId}
          onChange={(e) => applyTemplate(e.target.value)}
        >
          {TEMPLATES.map((t) => (
            <option key={t.id} value={t.id}>{t.label}</option>
          ))}
        </select>

        {isFeeTemplate && (
          <div style={styles.feeBox}>
            {selected.length !== 1 && (
              <div style={styles.errorMsg}>This template requires exactly one tenant selected.</div>
            )}
            {tenantCapRequests.length > 0 && (
              <>
                <label style={{ fontSize: 11, color: colors.textSecondary, display: "block", marginBottom: 4 }}>
                  Which pending request is this for? (optional)
                </label>
                <select
                  style={{ ...inputBase, width: "100%", marginBottom: space[2], boxSizing: "border-box" }}
                  value={selectedCapRequestId}
                  onChange={(e) => setSelectedCapRequestId(e.target.value)}
                >
                  <option value="">Not tied to a specific request</option>
                  {tenantCapRequests.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.field_name}: {r.requested_value}
                    </option>
                  ))}
                </select>
              </>
            )}
            <label style={{ fontSize: 11, color: colors.textSecondary, display: "block", marginBottom: 6 }}>
              Subscription fee by item. Enter $0 for anything not being charged:
            </label>
            {CAP_FEE_ROWS.map((row, i) => (
              <div key={row.label} style={styles.capFeeRow}>
                <span>{row.label}</span>
                <input
                  style={{ ...inputBase, width: 90, boxSizing: "border-box" }}
                  type="number"
                  min="0"
                  step="0.01"
                  value={capFees[i]}
                  onChange={(e) => {
                    const next = [...capFees];
                    next[i] = e.target.value;
                    setCapFees(next);
                  }}
                />
              </div>
            ))}
            <div style={styles.capFeeTotalRow}>
              <span>Total Subscription Fee</span>
              <span>${feeTotal.toFixed(2)}</span>
            </div>
            <p style={{ fontFamily: fonts.body, fontSize: 11, color: colors.textMuted, marginTop: space[2] }}>
              Use <code>{"{{FEE_AMOUNT}}"}</code>, <code>{"{{AGREE_LINK}}"}</code>, and{" "}
              <code>{"{{CAP_FEE_TABLE}}"}</code> in the message, and they'll be replaced with the
              total, a working "I Agree" link, and an itemized table automatically.
            </p>
          </div>
        )}

        <input
          style={{ ...inputBase, width: "100%", marginBottom: space[2], boxSizing: "border-box" }}
          placeholder="Subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        />
        <textarea
          style={{ ...inputBase, width: "100%", height: 140, boxSizing: "border-box", marginBottom: space[3] }}
          placeholder="Message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
        <div style={{ display: "flex", gap: space[2] }}>
          <button style={{ ...buttonSecondary, flex: 1 }} onClick={onClose}>Cancel</button>
          <button style={{ ...buttonPrimary, flex: 1 }} onClick={send} disabled={sending}>
            {sending ? "Sending…" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}

const FIELD_LABELS = {
  max_categories: "Max Categories",
  max_items_per_category: "Max Items per Category",
  max_tables: "Max Tables",
  max_staff: "Max Staff Accounts",
  max_employees: "Max Employees",
  attendance_enabled: "Employee Module",
  online_ordering_enabled: "Online Ordering",
  online_pay_stripe_allowed: "Online Card Payments",
  report_retention_days: "Report Retention (days)",
  split_bill_enabled: "Split Bill",
};

function StatusPill({ status }) {
  const map = {
    pending: { bg: "#fef3c7", color: "#92400e" },
    approved: { bg: "#dcfce7", color: "#166534" },
    rejected: { bg: "#fee2e2", color: "#991b1b" },
  };
  const s = map[status] || { bg: colors.surfaceMuted, color: colors.textSecondary };
  return (
    <span style={{
      background: s.bg, color: s.color, padding: "2px 10px",
      borderRadius: radius.pill, fontSize: 12, fontWeight: 600,
    }}>
      {status}
    </span>
  );
}

function CapField({ label, value, onChange, step = "1" }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={{ fontSize: 11, color: colors.textSecondary }}>{label}</label>
      <input
        type="number"
        min="0"
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        style={{ width: 90, padding: "6px 8px", borderRadius: radius.sm, border: `1px solid ${colors.border}` }}
      />
    </div>
  );
}

function TextField({ label, value, onChange }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={{ fontSize: 11, color: colors.textSecondary }}>{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ width: 160, padding: "6px 8px", borderRadius: radius.sm, border: `1px solid ${colors.border}` }}
      />
    </div>
  );
}

const styles = {
  th: { padding: `${space[3]} ${space[2]}`, fontSize: 13, color: colors.textSecondary },
  thSortable: { padding: `${space[3]} ${space[2]}`, fontSize: 13, color: colors.textSecondary, cursor: "pointer", userSelect: "none" },
  td: { padding: `${space[2]} ${space[2]}`, fontSize: 14, color: colors.textPrimary },
  actionBtn: {
    padding: "6px 10px", fontSize: 12, borderRadius: radius.sm,
    border: `1px solid ${colors.border}`, background: colors.surfaceMuted, cursor: "pointer",
  },
  deleteBtn: {
    padding: "6px 10px", fontSize: 12, borderRadius: radius.sm,
    border: "1px solid #fecaca", background: "#fef2f2", color: "#b91c1c", cursor: "pointer",
  },
  pendingEmailNote: { fontSize: 11, color: colors.statusProgress, marginTop: 2 },
  feeBox: { border: `1px dashed ${colors.border}`, borderRadius: radius.sm, padding: space[2], marginBottom: space[2] },
  capFeeRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "3px 0", fontFamily: fonts.body, fontSize: 13 },
  capFeeTotalRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", marginTop: 4, borderTop: `1px solid ${colors.border}`, fontFamily: fonts.display, fontWeight: 700, fontSize: 14 },
  agreedFeeNote: { fontSize: 11, color: colors.statusReady, marginTop: 2, fontWeight: 600 },
  sortRow: { display: "flex", gap: space[2], alignItems: "center", marginBottom: space[3] },
  tenantCard: { background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: space[3], marginBottom: space[3] },
  cardName: { fontFamily: fonts.display, fontWeight: 600, fontSize: 16, color: colors.textPrimary, wordBreak: "break-word" },
  cardMeta: { fontSize: 12, color: colors.textSecondary, marginTop: 2, wordBreak: "break-all" },
  cardFacts: { display: "grid", gridTemplateColumns: "auto 1fr", gap: "4px 14px", fontSize: 13, color: colors.textPrimary, margin: `${space[3]} 0`, paddingTop: space[3], borderTop: `1px solid ${colors.border}` },
  cardFactLabel: { color: colors.textSecondary },
  cardActions: { display: "flex", gap: 6, flexWrap: "wrap" },
  cardEditor: { marginTop: space[3], padding: space[3], background: colors.surfaceMuted, borderRadius: radius.sm },
  pillOn: { padding: "2px 9px", fontSize: 11, fontWeight: 600, borderRadius: 999, border: "1px solid #86efac", background: "#dcfce7", color: "#166534", cursor: "pointer", whiteSpace: "nowrap" },
  pillOff: { padding: "2px 9px", fontSize: 11, fontWeight: 600, borderRadius: 999, border: `1px solid ${colors.border}`, background: colors.surfaceMuted, color: colors.textSecondary, cursor: "pointer", whiteSpace: "nowrap" },
  archiveStats: { display: "flex", gap: 16, flexWrap: "wrap", padding: "8px 10px", background: colors.surfaceMuted, borderRadius: radius.sm },
  archiveField: { display: "flex", flexDirection: "column", gap: 2, fontSize: 12, color: colors.textSecondary },
  archiveInput: { padding: "6px 8px", border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: 13 },
  actionCell: { display: "inline-flex", gap: 6, alignItems: "center", justifyContent: "flex-end" },
  moreBtn: { padding: "4px 10px", fontSize: 16, lineHeight: 1, borderRadius: radius.sm, border: `1px solid ${colors.border}`, background: colors.surfaceMuted, cursor: "pointer", fontWeight: 700 },
  menu: { position: "fixed", zIndex: 1200, width: 200, background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md, boxShadow: "0 8px 24px rgba(0,0,0,0.18)", padding: 4, textAlign: "left" },
  menuItem: { display: "block", width: "100%", textAlign: "left", padding: "8px 12px", fontSize: 13, background: "none", border: "none", borderRadius: radius.sm, cursor: "pointer", color: colors.textPrimary, whiteSpace: "nowrap" },
  strip: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: space[3], flexWrap: "wrap", background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: `${space[2]} ${space[4]}`, marginBottom: space[3], fontSize: 13 },
  stripSummary: { color: colors.textMuted, fontSize: 12 },
  filterBar: { display: "flex", gap: space[2], alignItems: "center", flexWrap: "wrap", marginBottom: space[2] },
  chips: { display: "flex", gap: 6, flexWrap: "wrap", marginBottom: space[3] },
  chip: { padding: "5px 12px", fontSize: 12, borderRadius: 999, border: `1px solid ${colors.border}`, background: colors.surface, color: colors.textPrimary, cursor: "pointer" },
  chipActive: { padding: "5px 12px", fontSize: 12, borderRadius: 999, border: `1px solid ${colors.brandBlue}`, background: colors.brandBlue, color: "#fff", cursor: "pointer", fontWeight: 600 },
  cellSub: { fontSize: 12, color: colors.textMuted, marginTop: 2, wordBreak: "break-all" },
  pager: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: space[3], margin: `${space[3]} 0`, fontSize: 13, color: colors.textSecondary },
  schemaBanner: { background: "#fef2f2", border: "1px solid #fca5a5", color: "#991b1b", borderRadius: radius.md, padding: space[3], margin: `${space[3]} 0`, fontSize: 13, lineHeight: 1.5 },
  drawerBackdrop: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 1040 },
  drawer: { position: "fixed", top: 0, right: 0, bottom: 0, width: 480, maxWidth: "100vw", background: colors.surface, boxShadow: "-8px 0 24px rgba(0,0,0,0.18)", zIndex: 1050, overflowY: "auto", boxSizing: "border-box" },
  drawerHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: space[2], padding: `${space[3]} ${space[4]}`, borderBottom: `1px solid ${colors.border}`, position: "sticky", top: 0, background: colors.surface },
  payBtn: {
    padding: "6px 10px", fontSize: 12, borderRadius: radius.sm, fontWeight: 600,
    border: `1px solid ${colors.statusReady}`, background: colors.statusReady, color: "#fff", cursor: "pointer",
  },
  badge: { display: "inline-block", marginLeft: 6, padding: "1px 8px", borderRadius: radius.pill, background: colors.statusProgress, color: "#fff", fontSize: 12, fontWeight: 600 },
  approveEmailBtn: {
    fontSize: 10, padding: "1px 6px", borderRadius: radius.sm,
    border: `1px solid ${colors.border}`, background: colors.surfaceMuted, cursor: "pointer",
  },
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
  modal: { width: 420, maxWidth: "calc(100vw - 24px)", boxSizing: "border-box", maxHeight: "92vh", overflowY: "auto", background: colors.surface, borderRadius: radius.md, padding: space[5] },
  errorMsg: { color: "#b91c1c", fontSize: 13, marginBottom: space[2] },
  capRequestRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: `${space[2]} 0`, borderBottom: `1px solid ${colors.border}`, fontFamily: fonts.body, fontSize: 13, color: colors.textPrimary },
};
